import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { useAuth } from "../App";
import { toast } from "sonner";

export default function AuthCallback() {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const params = new URLSearchParams(hash.substring(1));
    const sessionId = params.get("session_id");

    // Enhanced logging for debugging
    console.log("AuthCallback triggered:", {
      fullUrl: window.location.href,
      hash: hash,
      sessionId: sessionId ? sessionId.substring(0, 10) + '...' : 'MISSING',
      hasSessionId: !!sessionId,
      isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent),
      userAgent: navigator.userAgent
    });

    if (!sessionId) {
      console.error("No session_id in callback URL", { hash, fullUrl: window.location.href });
      setError("Authentication failed: No session ID received");
      toast.error("No session ID found in URL. Check console for details.");
      setTimeout(() => navigate("/login"), 2000);
      return;
    }

    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    (async () => {
      try {
        // Mobile-specific: Add retry logic for flaky mobile network conditions
        let attempts = 0;
        let lastError;
        
        while (attempts < 2) {
          try {
            console.log(`Auth attempt ${attempts + 1}/2:`, {
              sessionId: sessionId.substring(0, 10) + '...',
              apiUrl: `${API}/auth/google-session`
            });

            const { data } = await axios.post(
              `${API}/auth/google-session`,
              { session_id: sessionId },
              { 
                withCredentials: true,
                timeout: 10000, // 10 second timeout for mobile networks
                headers: {
                  'Content-Type': 'application/json',
                }
              }
            );
            
            console.log("Auth successful:", { userId: data.user_id, email: data.email });
            setUser(data);
            toast.success("Signed in successfully!");
            
            // Wait for cookie to be committed — mobile Safari needs longer
            const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
            await new Promise(resolve => setTimeout(resolve, isMobile ? 400 : 100));
            
            // If there's a pending checkout intent (from Google auth on checkout page),
            // go back to checkout so it can resume the order automatically.
            const hasCheckoutIntent = !!localStorage.getItem("rivora_checkout_intent");
            if (hasCheckoutIntent) {
              navigate("/checkout", { replace: true });
            } else {
              // Return to whatever page the user was on when they clicked Google sign-in
              const returnPath = localStorage.getItem("rivora_google_return_path") || "/account";
              localStorage.removeItem("rivora_google_return_path");
              navigate(returnPath, { replace: true });
            }
            return;
          } catch (err) {
            console.error(`Auth attempt ${attempts + 1} failed:`, {
              message: err.message,
              status: err.response?.status,
              statusText: err.response?.statusText,
              data: err.response?.data,
              isNetworkError: err.code === 'ERR_NETWORK',
              isTimeout: err.code === 'ECONNABORTED'
            });
            lastError = err;
            attempts++;
            if (attempts < 2) {
              // Wait before retry
              await new Promise(resolve => setTimeout(resolve, 500));
            }
          }
        }
        
        throw lastError;
        
      } catch (error) {
        const errorDetails = {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
          isMobile: /iPhone|iPad|iPod|Android/i.test(navigator.userAgent)
        };
        console.error("Google auth failed after retries:", errorDetails);
        
        // Show user-friendly error message
        if (error.code === 'ERR_NETWORK') {
          setError("Network error. Please check your connection and try again.");
          toast.error("Network error. Check your connection.");
        } else if (error.response?.status === 400) {
          setError("Authentication expired. Please try signing in again.");
          toast.error("Authentication expired. Please try again.");
        } else {
          setError("Sign in failed. Please try again.");
          toast.error(`Sign in failed: ${error.message}`);
        }
        
        setTimeout(() => navigate("/login"), 3000);
      }
    })();
  }, [navigate, setUser]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
      <div className="text-center max-w-md px-6">
        {!error ? (
          <>
            <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-zinc-900 border-r-transparent"></div>
            <p className="mt-4 text-sm text-zinc-600">Signing you in...</p>
            <p className="mt-2 text-xs text-zinc-400">Please wait while we complete authentication</p>
          </>
        ) : (
          <>
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <p className="text-sm text-zinc-900 font-medium mb-2">{error}</p>
            <p className="text-xs text-zinc-500">Redirecting to login...</p>
          </>
        )}
      </div>
    </div>
  );
}
